<template>
  <div
    :class="
      classes(
        n(),
        n(`--${state.type}`),
        n(`--${state.background}`),
        n(`--${state.size}`),
        [isLiteapp, n('--liteapp')],
      )
    "
    :style="tabsStyle"
  >
    <!-- 标签栏区域 -->
    <div
      :class="
        classes(
          n('nav-wrapper'),
          [state.showGradient && showLeftGradient, n('nav-wrapper--gradient-left')],
          [state.showGradient && showRightGradient, n('nav-wrapper--gradient-right')],
        )
      "
    >
      <!-- 左侧插槽 -->
      <div
        v-if="$slots['nav-left']"
        :class="n('nav-left')"
      >
        <slot
          name="nav-left"
          :active-index="activeIndex"
        />
      </div>

      <!-- LiteApp：遮罩作用在普通 div 容器 nav-mask 上（mask 加在普通 DOM 可正常重绘），
           绝不可加在 scroll-view 原生组件上（动态切换不重绘、残留旧遮罩）。无颜色、真·边缘淡出。 -->
      <div
        v-if="isLiteapp"
        ref="navMaskRef"
        :class="n('nav-mask')"
      >
        <scroll-view
          ref="tabsNavRef"
          :class="n('nav')"
          scroll-x
          :scroll-left="scrollLeft"
          :scroll-with-animation="scrollWithAnimation"
          :show-scrollbar="false"
          scroll-config="clamping"
          @scroll="handleScrollViewScroll"
          @scrollend="handleScrollViewScrollEnd"
        >
          <div
            :class="classes(n('nav-track'), [props.align === 'center', n('nav-track--center')])"
            :style="navTrackStyle"
          >
            <slot />
            <!-- 底部指示器 (仅 normal 类型) -->
            <div
              v-if="state.type === 'normal'"
              ref="indicatorRef"
              :class="n('indicator')"
              :style="indicatorStyle"
            />
          </div>
        </scroll-view>
      </div>

      <!-- 标签栏 - H5 环境使用原生 div -->
      <div
        v-else
        ref="tabsNavRef"
        :class="n('nav')"
        @scroll="handleScroll"
      >
        <div
          :class="classes(n('nav-track'), [props.align === 'center', n('nav-track--center')])"
          :style="navTrackStyle"
        >
          <slot />
          <!-- 底部指示器 (仅 normal 类型) -->
          <div
            v-if="state.type === 'normal'"
            ref="indicatorRef"
            :class="n('indicator')"
            :style="indicatorStyle"
          />
        </div>
      </div>

      <!-- 右侧插槽 -->
      <div
        v-if="$slots['nav-right']"
        :class="n('nav-right')"
      >
        <slot
          name="nav-right"
          :active-index="activeIndex"
        />
      </div>
    </div>

    <!-- 内容区域 -->
    <div
      v-if="state.swipeable"
      :class="n('content')"
    >
      <div
        :class="n('content-track')"
        :style="{
          transform: `translateX(-${activeIndexNum * 100}%)`,
          transition: state.animated ? `transform ${state.duration}ms ease` : 'none',
        }"
      >
        <slot name="content" />
      </div>
    </div>
  </div>
</template>

<script lang="ts">
import { computed, defineComponent, nextTick, onBeforeUnmount, onMounted, provide, ref, toRefs, watch } from 'virtual-vue';
import { tabsProps } from './props';
import { tabsEmits } from './emits';
import { TABS_INJECT_KEY } from './provide';
import { useVModel } from '../hooks/use-v-model';
import { createNamespace } from '../utils/components';
import { platform } from '@tencent/wphk-platform-adapter';
import type { TabsIndex } from './tabs';

declare const lite: {
  addEventListener?: (_type: string, _listener: () => void) => void;
  removeEventListener?: (_type: string, _listener: () => void) => void;
};

const { name, n, classes } = createNamespace('tabs');

export default defineComponent({
  name,
  props: tabsProps,
  emits: tabsEmits,
  setup(props, { emit }) {
    // ==================== 响应式状态定义 ====================
    const activeIndex = useVModel(props);
    const tabsNavRef = ref<HTMLElement>();
    const navMaskRef = ref<HTMLElement>();
    const indicatorRef = ref<HTMLElement>();
    const hasOverflow = ref(false);
    const showLeftGradient = ref(false);
    const showRightGradient = ref(false);
    /**
     * 端点状态的「滞回记忆」：
     * - atStartState = true 表示当前处于最左端（左遮罩应隐藏）
     * - atEndState   = true 表示当前处于最右端（右遮罩应隐藏）
     * 进入端点用窄阈值（实时隐藏，满足「滑到两端即隐藏」），
     * 离开端点用宽阈值（吸收端点附近 scrollLeft 抖动，防止「先隐藏后展示」）。
     * 初始：left 在端点(true)、right 不在(false)。
     */
    const atStartState = ref(true);
    const atEndState = ref(false);
    /** 缓存上次应用的 mask 字符串，mask 未变时不重复赋值，避免逐帧重绘 */
    let navMaskCache = '';

    // ==================== Liteapp 环境适配 ====================
    // Liteapp 和小程序环境不支持 overflow: scroll，需要使用 scroll-view 组件
    const isLiteapp = platform.isLiteapp();
    /** scroll-view 组件的横向滚动位置（仅 liteapp 环境使用） */
    const scrollLeft = ref(0);
    /** scroll-view 是否启用滚动动画（仅 liteapp 环境使用） */
    const scrollWithAnimation = ref(false);
    /**
     * 记录程序化滚动的目标位置，用于区分程序化滚动和用户手动滚动
     * - null: 表示当前没有程序化滚动，或者程序化滚动已结束
     * - number: 表示正在进行程序化滚动，值为目标滚动位置
     *
     * 这个标记的作用：
     * 1. 防止程序化滚动时触发不必要的 scroll 事件
     * 2. 避免响应式更新导致的滚动循环问题
     */
    const lastProgrammaticScrollLeft = ref<number | null>(null);
    /** LiteApp 最近一次有效的滚动位置，避免 scrollend 缺失位置时回退为 0 */
    let latestLiteappScrollLeft = 0;
    /** 最近一次有效布局计算得到的最大滚动距离，避免滚动结束时瞬时尺寸异常 */
    let maxScrollLeft = 0;

    // ==================== 计算属性 ====================
    /** 组件状态的计算属性集合 */
    const state = computed(() => ({
      type: props.type,              // 标签类型：normal | button | card
      background: props.background,  // 背景色：white | gray
      size: props.size,              // 尺寸：small | medium | large
      showGradient: props.showGradient,  // 是否显示渐变遮罩
      swipeable: props.swipeable,    // 是否支持滑动切换
      animated: props.animated,      // 是否启用切换动画
      duration: props.duration,      // 动画持续时间
    }));

    /** 将 activeIndex 转换为数字类型，用于计算内容区域的偏移 */
    const activeIndexNum = computed(() => {
      const index = Number(activeIndex.value);
      return isNaN(index) ? 0 : index;
    });

    /**
     * 标签栏的自定义样式
     * 通过 CSS 变量的方式注入激活和非激活状态的颜色
     */
    const tabsStyle = computed(() => {
      const style: Record<string, string> = {};
      if (props.activeColor) {
        style['--tabs-active-color'] = props.activeColor;
      }
      if (props.inactiveColor) {
        style['--tabs-inactive-color'] = props.inactiveColor;
      }
      return style;
    });

    /**
     * 底部指示器的动态样式（位置、宽度和颜色）
     *
     * 初始值即带上自定义颜色：指示器颜色从首帧就固定，后续 updateIndicator 只更新位置/宽度、颜色不变，
     * 因此 transition 不会对颜色做动画，避免 LiteApp 下「默认色 → 自定义色」的渐变过程。
     */
    const indicatorStyle = ref<Record<string, string>>(
      props.indicatorColor ? { backgroundColor: props.indicatorColor } : {},
    );

    /** 导航视口像素宽度（LiteApp 居中场景由 JS 测量得到） */
    const navMinWidth = ref(0);
    /** LiteApp 居中布局是否测量就绪（就绪前隐藏 nav-track，避免「先左后中」的跳变） */
    const centerLayoutReady = ref(false);
    /** LiteApp 居中测量的重试计数 */
    let centerRetryCount = 0;

    /**
     * 导航轨道的动态样式
     *
     * - H5：不需要内联样式，直接依赖 CSS `min-width: 100%`（百分比可正常解析，首帧即居中，无闪烁）；
     * - LiteApp：CSS 百分比宽度在 scroll-view 内解析不到视口宽度，需内联 JS 测得的像素 min-width，
     *   且测量就绪前先隐藏，避免首帧左对齐随后跳到居中。
     */
    const navTrackStyle = computed<Record<string, string>>(() => {
      if (props.align !== 'center' || !isLiteapp) {
        return {};
      }
      const style: Record<string, string> = {};
      if (navMinWidth.value > 0) {
        style.minWidth = `${navMinWidth.value}px`;
      }
      // 测量就绪前先隐藏，避免第一帧左对齐、随后突然跳到居中的突兀过程
      if (!centerLayoutReady.value) {
        style.visibility = 'hidden';
      }
      return style;
    });

    /**
     * 更新居中布局（仅 LiteApp 需要）
     *
     * CSS 百分比宽度（min-width: 100%）在 LiteApp scroll-view 内无法解析到视口宽度，
     * 导致 justify-content: center 失效。H5 环境百分比可正常解析，无需 JS 干预。
     *
     * LiteApp 处理：
     * 1. JS 测量导航视口像素宽度，作为 nav-track 的 min-width，使 justify-content 居中生效
     *    （内容未超屏：nav-track = 视口宽度，居中生效；内容超屏：撑到内容宽度，自然靠左滚动）；
     * 2. scroll-view 刚渲染时 clientWidth 可能为 0（刷新时尤为明显），多帧重试直至测到有效宽度，
     *    修复「有时不居中、刷新后变左对齐」问题；
     * 3. 测量就绪前隐藏 nav-track，修复「进页面先左后跳到中间」的跳变。
     */
    const updateCenterLayout = () => {
      if (props.align !== 'center' || !isLiteapp) {
        return;
      }
      const nav = tabsNavRef.value as HTMLElement | undefined;
      const navWidth = nav ? (nav.clientWidth || nav.offsetWidth || 0) : 0;
      if (navWidth > 0) {
        navMinWidth.value = navWidth;
        centerLayoutReady.value = true;
        centerRetryCount = 0;
        updateNavMask();
        return;
      }
      // 尚未测到有效宽度，下一帧重试（scroll-view 初始化 / 页面刷新场景）
      if (centerRetryCount < 20) {
        centerRetryCount += 1;
        setTimeout(updateCenterLayout, 16);
        return;
      }
      // 多次仍测不到宽度：兜底显示（左对齐），避免内容一直隐藏
      centerLayoutReady.value = true;
      centerRetryCount = 0;
    };

    /** 重新触发一轮居中测量（重置重试计数） */
    const scheduleCenterLayout = () => {
      centerRetryCount = 0;
      updateCenterLayout();
    };

    /**
     * LiteApp 下动态构建并应用 nav-mask 的 mask-image（像素值，非百分比）。
     *
     * 为什么必须 JS 驱动（而非 CSS 百分比）：
     * LiteApp 中 mask-image 的百分比 100% 会解析到「内容总宽」而非 .nav-mask 的视口宽，
     * 导致右侧渐变落点算错——遮罩看起来异常宽，且滑到最右时仍不消失。
     * 这里改为实测 .nav-mask 的像素宽，用像素拼 mask 渐变，落点精确、普通 DOM 动态切换可重绘。
     * H5 不受影响（仍走 CSS 百分比 mask，见 index.less）。
     */
    const updateNavMask = () => {
      if (!isLiteapp || !navMaskRef.value) {
        return;
      }
      const maskEl = navMaskRef.value as HTMLElement;
      const width = maskEl.clientWidth || maskEl.offsetWidth || 0;
      if (width <= 0) {
        return;
      }
      // 渐变宽度：从 .nav-mask 暴露的 CSS 变量 --tabs-gradient-width(rem) 换算成像素
      const gradWidthRem = parseFloat(
        getComputedStyle(maskEl).getPropertyValue('--tabs-gradient-width'),
      ) || 0.4; // 兜底 0.4rem（@tabs-gradient-width = 80*@unit = 0.4rem）
      // rem 基准：getComputedStyle 的 fontSize 即 1rem 的像素值
      const remPx = parseFloat(getComputedStyle(document.documentElement).fontSize) || 16;
      const gradPx = Math.round(gradWidthRem * remPx);
      const fadeStart = Math.max(width - gradPx, 0);

      const right = `linear-gradient(to right, #000 0%, #000 ${fadeStart}px, transparent ${width}px)`;
      const left = `linear-gradient(to right, transparent 0px, #000 ${gradPx}px, #000 100%)`;
      const both = `linear-gradient(to right, transparent 0px, #000 ${gradPx}px, #000 ${fadeStart}px, transparent ${width}px)`;

      let mask: string;
      if (showLeftGradient.value && showRightGradient.value) {
        mask = both;
      } else if (showRightGradient.value) {
        mask = right;
      } else if (showLeftGradient.value) {
        mask = left;
      } else {
        // 无遮罩：清除，露出完整内容（去重：已是 none 则不重复赋值）
        if (navMaskCache !== 'none') {
          maskEl.style.webkitMaskImage = 'none';
          maskEl.style.maskImage = 'none';
          navMaskCache = 'none';
        }
        return;
      }
      // 去重：mask 字符串未变时不重复赋值，避免逐帧重绘引发闪动
      if (navMaskCache === mask) {
        return;
      }
      maskEl.style.webkitMaskImage = mask;
      maskEl.style.maskImage = mask;
      navMaskCache = mask;
    };

    // ==================== 核心方法 ====================

    /**
     * 更新激活的标签
     * @param index - 标签索引
     * @param title - 标签标题（可选）
     *
     * 执行流程：
     * 1. 检查是否需要更新（避免重复更新）
     * 2. 更新 activeIndex 状态
     * 3. 触发 change 事件
     * 4. 在下一帧更新指示器位置和滚动位置
     */
    const updateActive = (index: TabsIndex, title?: string) => {
      // 如果点击的是当前激活的标签，不做任何处理
      if (activeIndex.value === index) {
        return;
      }

      // 更新激活索引
      activeIndex.value = index;

      // 触发 change 事件，通知父组件
      emit('change', index, title || '');

      // 在 DOM 更新后执行后续操作
      nextTick(() => {
        updateIndicator();      // 更新底部指示器位置
        scrollToCenter(index);  // 滚动到合适位置
      });
    };

    /**
     * 更新底部指示器的位置和宽度（仅 normal 类型使用）
     *
     * 计算逻辑：
     * 1. 找到当前激活的标签元素
     * 2. 获取标签的位置和尺寸信息
     * 3. 计算指示器的宽度（基于内容宽度，限制在 48-112px 之间）
     * 4. 计算指示器的水平位置（居中对齐）
     * 5. 通过 transform 和 width 样式更新指示器
     */
    const updateIndicator = () => {
      // 只有 normal 类型才有底部指示器
      if (state.value.type !== 'normal' || !tabsNavRef.value) {
        return;
      }

      // 获取导航轨道容器
      // 兼容 liteapp 环境，使用 querySelector 或 children[0]
      const navTrack = tabsNavRef.value.querySelector(`.${n('nav-track')}`) || (tabsNavRef.value as HTMLElement).children[0] as HTMLElement;
      if (!navTrack) {
        return;
      }

      // 获取所有标签项（从 nav-track 中查找）
      const items = Array.from(navTrack.querySelectorAll(`.${n('item')}`));

      // 找到当前激活的标签项
      const activeItem = items.find((item: any) => item.dataset.index === String(activeIndex.value)) as HTMLElement;

      if (activeItem) {
        // 获取文本标题元素（item-title），指示器宽度应该基于文本宽度
        const titleElement = activeItem.querySelector(`.${n('item-title')}`) as HTMLElement;
        if (!titleElement) {
          return;
        }

        if (isLiteapp) {
          // ===== Liteapp 环境 =====
          // 在 liteapp 中，使用 offsetLeft 和 offsetWidth 计算位置
          // 因为指示器和标签都在 nav-track 内部，所以相对于 nav-track 计算即可
          const itemLeft = activeItem.offsetLeft;

          // 计算指示器宽度
          // 如果传入了 indicatorWidth，优先使用传入的值，否则使用文本宽度
          const textWidth = titleElement.offsetWidth;
          const baseWidth = props.indicatorWidth !== undefined ? props.indicatorWidth : textWidth;

          // 指示器宽度限制在 48-112px 之间
          // 默认与文本宽度一致，最小 48px，最大 112px
          const width = Math.min(Math.max(baseWidth, 48), 112);

          // 计算指示器的左侧位置（相对于 nav-track）
          // 指示器应该居中对齐在 item 内部
          // 位置 = item左侧 + (item宽度 - 指示器宽度) / 2
          const itemWidth = activeItem.offsetWidth;
          const left = itemLeft + (itemWidth - width) / 2;

          // 构建指示器样式
          const style: Record<string, string> = {
            transform: `translateX(${left}px)`,
            width: `${width}px`,
          };

          // 自定义颜色：保持与初始值一致（颜色不变，不触发 transition 渐变）
          if (props.indicatorColor) {
            style.backgroundColor = props.indicatorColor;
          }

          indicatorStyle.value = style;
        } else {
          // ===== H5 环境 =====
          // 在 H5 中，使用 getBoundingClientRect 计算位置
          const navRect = tabsNavRef.value.getBoundingClientRect();
          const itemRect = activeItem.getBoundingClientRect();
          const titleRect = titleElement.getBoundingClientRect();

          // 计算指示器宽度
          // 如果传入了 indicatorWidth，优先使用传入的值，否则使用文本宽度
          const textWidth = titleRect.width;
          const baseWidth = props.indicatorWidth !== undefined ? props.indicatorWidth : textWidth;

          // 指示器宽度限制在 48-112px 之间
          // 默认与文本宽度一致，最小 48px，最大 112px
          const width = Math.min(Math.max(baseWidth, 48), 112);

          // 计算指示器的左侧位置（相对于导航栏容器）
          // 指示器应该居中对齐在 item 内部
          const currentScrollLeft = (tabsNavRef.value as HTMLElement).scrollLeft || 0;
          const itemCenter = itemRect.left - navRect.left + currentScrollLeft + itemRect.width / 2;
          const left = itemCenter - width / 2;

          // 构建指示器样式
          const style: Record<string, string> = {
            transform: `translateX(${left}px)`,
            width: `${width}px`,
          };

          // 自定义颜色：保持与初始值一致（颜色不变，不触发 transition 渐变）
          if (props.indicatorColor) {
            style.backgroundColor = props.indicatorColor;
          }

          // 更新指示器样式
          indicatorStyle.value = style;
        }
      }
    };

    /**
     * 自动滚动到指定标签，使其居中显示
     * @param index - 目标标签索引
     *
     * 滚动策略：
     * - 前 4 个标签不触发滚动（通常能完整显示）
     * - 其他标签滚动到容器中心位置
     *
     * 环境适配：
     * - H5: 使用原生 scrollTo API
     * - Liteapp: 使用 scroll-view 组件的 scroll-left 属性
     */
    const scrollToCenter = (index: TabsIndex) => {
      // 将索引转换为数字
      const indexNum = typeof index === 'number' ? index : parseInt(String(index), 10);

      // 前 4 个标签不滚动（优化体验，避免不必要的滚动）
      if (indexNum < 4) {
        return;
      }

      const container = tabsNavRef.value;
      if (!container) {
        return;
      }

      // 获取导航轨道容器
      // 兼容 liteapp 环境，使用 querySelector 或 children[0]
      const navTrack = container.querySelector(`.${n('nav-track')}`) || container.children[0];
      if (!navTrack) {
        return;
      }

      // 查找目标标签元素
      // 兼容 liteapp 环境，使用遍历方式而非 querySelector
      // 因为 liteapp 对属性选择器的支持不完整
      let targetTab: HTMLElement | null = null;
      for (let i = 0; i < navTrack.children.length; i++) {
        const child = navTrack.children[i] as HTMLElement;
        const dataIndex = child.getAttribute('data-index');
        if (dataIndex === String(index)) {
          targetTab = child;
          break;
        }
      }

      if (!targetTab) {
        return;
      }

      // 获取容器和目标标签的尺寸信息
      const containerWidth = container.offsetWidth;  // 容器可视宽度
      const targetLeft = targetTab.offsetLeft;       // 目标标签左侧位置
      const targetWidth = targetTab.offsetWidth;     // 目标标签宽度

      // 计算滚动位置，使目标标签居中
      // 公式：目标左侧位置 - (容器宽度 / 2) + (目标宽度 / 2)
      const targetScrollLeft = targetLeft - (containerWidth / 2) + (targetWidth / 2);

      // 确保滚动位置不小于 0
      const finalScrollLeft = Math.max(0, targetScrollLeft);

      if (isLiteapp) {
        // ===== Liteapp 环境 =====
        // 使用 scroll-view 组件的响应式属性进行滚动

        // 1. 记录程序化滚动的目标位置（用于后续事件处理中的判断）
        lastProgrammaticScrollLeft.value = finalScrollLeft;

        // 2. 启用滚动动画
        scrollWithAnimation.value = true;

        // 3. 更新滚动位置（触发 scroll-view 的滚动）
        //    注意：这是唯一更新 scrollLeft.value 的地方，避免响应式循环
        scrollLeft.value = finalScrollLeft;
      } else {
        // ===== H5 环境 =====
        // 使用原生 scrollTo API，带平滑动画
        container.scrollTo({
          left: finalScrollLeft,
          behavior: 'smooth',
        });
      }
    };

    /**
     * 溢出测量的重试计数（LiteApp 首帧布局未就绪时，navTrack.scrollWidth 可能为 0 或偏小，
     * 导致初始 maxScrollLeft 误判为 0。用重试机制在拿到有效尺寸后再做一次性测量，
     * 替代原先「滚动中 maxScrollLeft===0 兜底重测」的写法，从根上消除端点遮罩闪动）。
     */
    let overflowRetryCount = 0;
    const OVERFLOW_RETRY_MAX = 20;

    /**
     * 延迟重试一次溢出测量（仅 LiteApp、且仅在尺寸无效时使用）。
     * 参考 updateCenterLayout 的逐帧重试：scroll-view 初始化/页面刷新时 clientWidth 可能为 0，
     * 多帧重试直至测到有效尺寸，避免 maxScrollLeft 卡在 0、后续滚动每帧重测引发闪动。
     */
    const scheduleOverflowMeasure = () => {
      if (overflowRetryCount < OVERFLOW_RETRY_MAX) {
        overflowRetryCount += 1;
        setTimeout(() => checkOverflow(undefined, true), 16);
      }
    };

    /**
     * 检查标签栏是否溢出，并更新渐变遮罩的显示状态
     * @param currentScrollLeft - 当前滚动位置（可选，用于避免重复读取响应式变量）
     * @param shouldMeasure - 是否重新测量滚动尺寸；scroll/scrollend 传 false，沿用缓存
     *
     * 遮罩显示逻辑：
     * - 左侧遮罩：内容溢出 && 不在最左侧
     * - 右侧遮罩：内容溢出 && 不在最右侧
     *
     * 闪动根因与修复：
     * 原逻辑测量守卫为 `shouldMeasure || maxScrollLeft === 0`，在滚动中只要 maxScrollLeft
     * 为 0 就会逐帧重测 LiteApp 的 trackScrollWidth。该值在每帧不稳定、会抖动，使
     * maxScrollLeft 时大时小，atEnd 在 true↔false 间翻转，造成端点遮罩「先隐藏后展示」。
     * 修复：仅在 shouldMeasure 为 true（mount/resize/align 等结构性变化）时测量；
     * 测量值仅在「尺寸有效」时采纳，无效(宽/高为0,多为测量过早)则保留历史值并延迟重试，
     * 绝不在滚动过程中清零或重测 maxScrollLeft，从而 endDeadZone/atEnd 判定始终稳定。
     */
    const checkOverflow = (
      currentScrollLeft?: number,
      shouldMeasure = true,
    ) => {
      if (!tabsNavRef.value) {
        return;
      }
      const nav = tabsNavRef.value;

      // 仅在结构性变化时测量（scroll/scrollend 事件传 false，沿用缓存的 maxScrollLeft）。
      // 关键修复：不再用 `maxScrollLeft === 0` 在滚动中兜底重测，
      // 避免 LiteApp trackScrollWidth 逐帧抖动让 maxScrollLeft 时大时小、端点遮罩闪动。
      if (shouldMeasure) {
        let measuredOverflow = false;
        let measuredMax = 0;
        let measuredValid = false;

        if (isLiteapp) {
          // LiteApp 的 scrollWidth 和边界事件在不同包裹结构下不稳定，
          // 使用组件内已验证可用的 Tab 几何位置计算真实滚动范围。
          const navTrack = nav.querySelector(`.${n('nav-track')}`) || nav.children[0] as HTMLElement;
          const items = navTrack
            ? Array.from(navTrack.querySelectorAll(`.${n('item')}`)) as HTMLElement[]
            : [];
          const lastItem = items[items.length - 1];
          const viewport = nav.parentNode as HTMLElement | null;
          // 视口可视宽：优先 clientWidth（不含滚动条/边框，且 scroll-view 自身 overflow 已裁剪为视口宽）
          const viewportWidth = viewport?.clientWidth || nav.clientWidth || nav.offsetWidth || 0;
          // contentWidth 用 nav-track 自身的 scrollWidth，而非 lastItem.offsetLeft 累加：
          // LiteApp 的 scroll-view 不建立定位上下文，item 的 offsetParent 会跳到外层，
          // 使 offsetLeft 额外包含 nav-left/padding 等左偏移，导致 contentWidth 偏大、
          // maxScrollLeft 偏大、滚到最右也 isAtEnd 为 false、右侧遮罩不隐藏。
          const trackScrollWidth = navTrack ? (navTrack as HTMLElement).scrollWidth : 0;
          if (lastItem && viewportWidth > 0 && trackScrollWidth > 0) {
            measuredValid = true;
            measuredOverflow = trackScrollWidth > viewportWidth + 1;
            measuredMax = Math.max(trackScrollWidth - viewportWidth, 0);
          }
        } else {
          const { scrollWidth, clientWidth } = nav;
          if (scrollWidth > 0 && clientWidth > 0) {
            measuredValid = true;
            measuredOverflow = scrollWidth > clientWidth;
            measuredMax = Math.max(scrollWidth - clientWidth, 0);
          }
        }

        // 尺寸有效才刷新溢出态：避免测量时机过早(宽/高为0，多为首帧布局未就绪)
        // 把已确认的溢出态清零为「无溢出、maxScrollLeft=0」。实测无溢出(如 resize 收窄)
        // 也会正确回落为 false，保证遮罩随布局变化实时更新。
        if (measuredValid) {
          hasOverflow.value = measuredOverflow;
          maxScrollLeft = measuredMax;
          overflowRetryCount = 0;
        } else if (isLiteapp) {
          // LiteApp 测量时机过早：延迟重试，直到拿到有效尺寸，
          // 替代原先「滚动中 maxScrollLeft===0 兜底重测」，从根消除端点闪动。
          scheduleOverflowMeasure();
        }
      }

      // 获取当前滚动位置
      // 优先使用传入的参数（避免读取响应式变量导致循环更新）
      const scrollPosition = currentScrollLeft !== undefined
        ? currentScrollLeft
        : (isLiteapp ? latestLiteappScrollLeft : nav.scrollLeft);

      // 判断滚动位置（端点的滞回判定）。
      //
      // 闪动根因（实测复现：tab 少不闪、tab 宽多时闪）：
      // LiteApp scroll-view 在端点附近的 scrollLeft 会抖动——惯性滚动收尾、scroll/scrollend
      // 事件上报的位置有波动，且抖动幅度随内容越宽（maxScrollLeft 越大）越大。
      // 若用单阈值死区：要「滑到两端即隐藏」需死区窄，但窄死区挡不住端点抖动，
      // 抖动一旦越过死区边界，atEnd 从 true 翻回 false → 右遮罩「先隐藏后展示」。
      //
      // 修复：滞回（hysteresis）。进入端点用窄阈值 ENTER（实时隐藏，无延迟）；
      // 离开端点用宽阈值 RELEASE（约为 ENTER 的 3 倍，吸收端点 scrollLeft 抖动），
      // 一旦判定在端点，后续抖动只要不越过更宽的 RELEASE 边界，就保持在端点态，
      // 不会再翻回「非端点」导致遮罩重新展示。
      const endEnterDeadZone = Math.max(6, maxScrollLeft * 0.06);
      const endReleaseDeadZone = Math.max(18, maxScrollLeft * 0.18);

      const atStart = atStartState.value
        ? scrollPosition <= endReleaseDeadZone            // 已在端点：用宽阈值离开（吸收抖动）
        : scrollPosition <= endEnterDeadZone;            // 不在端点：用窄阈值进入（实时隐藏）
      const atEnd = atEndState.value
        ? scrollPosition >= maxScrollLeft - endReleaseDeadZone
        : scrollPosition >= maxScrollLeft - endEnterDeadZone;

      // 记忆端点状态（供下一次滞回判定）
      atStartState.value = atStart;
      atEndState.value = atEnd;

      // 更新遮罩显示状态
      showLeftGradient.value = hasOverflow.value && !atStart;   // 左侧遮罩
      showRightGradient.value = hasOverflow.value && !atEnd;    // 右侧遮罩

      // LiteApp 下按最新遮罩状态重算并应用像素 mask（H5 走 CSS，无需处理）
      updateNavMask();
    };

    // ==================== 事件处理 ====================

    /**
     * 处理滚动事件 - H5 环境
     * @param event - 原生滚动事件
     *
     * 处理流程：
     * 1. 获取当前滚动位置
     * 2. 触发 scroll 事件（通知父组件）
     * 3. 更新渐变遮罩状态
     */
    const handleScroll = (event: Event) => {
      const target = event.target as HTMLElement;
      const currentScrollLeft = target.scrollLeft;

      // 触发 scroll 事件
      emit('scroll', currentScrollLeft);

      // 更新遮罩显示状态（传入当前滚动位置避免重复读取）
      checkOverflow(currentScrollLeft, true);
    };

    /**
     * 从 LiteApp scroll-view 事件中读取有效滚动信息。
     * scrollend 在部分版本中不携带 scrollLeft，不能使用 0 作为缺省值。
     */
    const getLiteappEventScrollMetrics = (event: any) => {
      const detail = event?.detail ?? event;
      const eventScrollLeft = detail?.scrollLeft;
      return {
        scrollLeft: typeof eventScrollLeft === 'number' && Number.isFinite(eventScrollLeft)
          ? eventScrollLeft
          : undefined,
      };
    };

    /**
     * 处理滚动事件 - Liteapp 环境
     * @param event - scroll-view 组件的滚动事件
     *
     * 处理流程：
     * 1. 获取当前滚动位置
     * 2. 更新渐变遮罩状态
     * 3. 仅在用户手动滚动时触发 scroll 事件（程序化滚动不触发）
     *
     * 注意：
     * - 程序化滚动时不触发事件，避免不必要的事件通知
     * - 通过 lastProgrammaticScrollLeft 标记区分滚动来源
     */
    const handleScrollViewScroll = (event: any) => {
      const { scrollLeft: currentScrollLeft } = getLiteappEventScrollMetrics(event);
      if (currentScrollLeft === undefined) {
        return;
      }
      latestLiteappScrollLeft = currentScrollLeft;

      // 更新遮罩状态（滚动过程中实时更新）。
      // 关键：mustMeasure=false —— 滚动中【绝不】重新测量 maxScrollLeft。
      // LiteApp scroll-view 的 trackScrollWidth 在每帧不稳定、会抖动，若滚动中重测会让
      // maxScrollLeft 时大时小，使 isAtEnd 在 true↔false 间翻转，导致端点遮罩
      // 「隐藏↔展示」闪动。maxScrollLeft 只在 mount/resize/align 等结构性变化时测量
      // （或为 0 时兜底测一次），滚动过程一律沿用缓存值。
      // scroll-view 已设 scroll-config="clamping" 关闭 overscroll 回弹，scrollLeft 不越界振荡，
      // 故直接按位置纯判定即可，进入端点死区即隐藏、中段即展示，实时无延迟、无闪动。
      checkOverflow(currentScrollLeft, false);

      // 只有在用户手动滚动时才发送 scroll 事件
      // 程序化滚动（点击标签触发）时不发送，避免不必要的事件通知
      if (lastProgrammaticScrollLeft.value === null) {
        emit('scroll', currentScrollLeft);
      }
    };

    /**
     * 处理滚动结束事件 - Liteapp 环境
     * @param event - scroll-view 组件的 scrollend 事件
     *
     * 处理流程：
     * 1. 关闭滚动动画
     * 2. 更新渐变遮罩状态
     * 3. 仅在用户手动滚动时触发 scroll 事件
     * 4. 清除程序化滚动标记
     *
     * 重要说明：
     * - 不要在这里更新 scrollLeft.value
     * - scrollLeft.value 只在 scrollToCenter 中更新，避免响应式滚动循环
     */
    const handleScrollViewScrollEnd = (_event: any) => {
      // 注意：不要拿 scrollend 事件自带的 scrollLeft 覆盖。LiteApp 的 scrollend 在惯性
      // 尚未结束时触发，其 detail.scrollLeft 是「手指抬起瞬间」的偏早位置，比滚动过程中
      // 最后一次 scroll 事件上报的真实最终位置更小 → 重算会让已隐藏的端点遮罩重新展示
      // （表现为「先隐藏后展示、最终仍展示」的闪动）。故直接沿用滚动过程中记录的
      // latestLiteappScrollLeft（真实最终位置）。
      const currentScrollLeft = latestLiteappScrollLeft;

      // 1. 关闭滚动动画
      scrollWithAnimation.value = false;

      // 2. 终态收敛(停稳)：沿用滚动过程中记录的 currentScrollLeft(真实最终位置)，
      // 不拿 scrollend 自带 scrollLeft。scroll-config="clamping" 已关闭回弹，
      // 故位置判定稳定，按真实最终位置收敛展示/隐藏即可。
      checkOverflow(currentScrollLeft, false);

      // 3. 如果是用户手动滚动结束，发送 scroll 事件
      if (lastProgrammaticScrollLeft.value === null) {
        emit('scroll', currentScrollLeft);
      }

      // 4. 清除程序化滚动标记（标记滚动已完成）
      lastProgrammaticScrollLeft.value = null;
    };

    // ==================== 依赖注入 ====================

    /**
     * 向子组件（TabItem）提供上下文数据
     * 子组件通过 inject(TABS_INJECT_KEY) 获取这些数据
     */
    const propsRefs = toRefs(props);
    provide(TABS_INJECT_KEY, {
      activeIndex,          // 当前激活的标签索引
      size: propsRefs.size,              // 标签尺寸
      type: propsRefs.type,              // 标签类型
      background: propsRefs.background,  // 背景色
      activeColor: propsRefs.activeColor,      // 激活颜色
      inactiveColor: propsRefs.inactiveColor,  // 非激活颜色
      updateActive,         // 更新激活状态的方法
    });

    // ==================== 生命周期和监听 ====================

    /**
     * 监听 activeIndex 变化
     * 当外部通过 v-model 改变 activeIndex 时，更新指示器位置
     */
    watch(activeIndex, () => {
      nextTick(() => {
        updateIndicator();
      });
    });

    /**
     * 监听 align 变化（动态切换对齐方式时）
     * LiteApp 居中：先隐藏 nav-track 再重新测量，避免切换瞬间的跳变
     */
    watch(() => props.align, () => {
      centerLayoutReady.value = false;
      scheduleCenterLayout();
      nextTick(() => {
        updateIndicator();
checkOverflow(undefined, true);
      });
    });

    /**
     * 屏幕 resize 时重新计算指示器位置和溢出状态
     * H5 环境：监听 window resize
     * LiteApp 环境：监听 lite resize
     */
    const handleResize = () => {
      scheduleCenterLayout();
      nextTick(() => {
        updateIndicator();
        checkOverflow(undefined, true);
      });
    };

    /**
     * 组件挂载后的初始化
     * 1. 更新指示器位置（定位到初始激活的标签）
     * 2. 检查是否溢出（决定是否显示渐变遮罩）
     * 3. 注册 resize 监听，确保屏幕尺寸变化后指示器位置正确
     *
     * 注意：在 liteapp 环境中，需要额外的延迟确保 DOM 完全渲染
     * 特别是在页面刷新后，scroll-view 组件可能需要更多时间初始化
     */
    onMounted(() => {
      // 先应用居中布局（设置 nav-track 像素级 min-width），
      // 再在下一帧根据最终布局计算指示器位置
      const init = () => {
        scheduleCenterLayout();
        nextTick(() => {
          updateIndicator();
          checkOverflow(undefined, true);
        });
      };

      nextTick(() => {
        if (isLiteapp) {
          setTimeout(init, 0);
        } else {
          init();
        }
      });

      if (isLiteapp) {
        if (typeof lite !== 'undefined' && typeof lite.addEventListener === 'function') {
          lite.addEventListener('resize', handleResize);
        }
      } else {
        window.addEventListener('resize', handleResize);
      }
    });

    onBeforeUnmount(() => {
      if (isLiteapp) {
        if (typeof lite !== 'undefined' && typeof lite.removeEventListener === 'function') {
          lite.removeEventListener('resize', handleResize);
        }
      } else {
        window.removeEventListener('resize', handleResize);
      }
    });

    // ==================== 返回模板使用的数据和方法 ====================

    return {
      // 工具函数
      n,                    // 生成 BEM 类名
      classes,              // 合并类名
      props,                // 组件 props（模板中使用 props.align）
      state,                // 组件状态集合
      activeIndex,          // 当前激活索引
      activeIndexNum,       // 激活索引的数字形式
      tabsStyle,            // 自定义样式
      tabsNavRef,           // 导航栏容器引用
      navMaskRef,           // LiteApp 遮罩容器引用
      indicatorRef,         // 指示器引用
      indicatorStyle,       // 指示器样式
      navTrackStyle,        // 导航轨道样式（居中布局）
      hasOverflow,          // 是否溢出
      showLeftGradient,     // 显示左侧遮罩
      showRightGradient,    // 显示右侧遮罩
      handleScroll,                 // H5 滚动事件
      handleScrollViewScroll,       // Liteapp 滚动事件
      handleScrollViewScrollEnd,    // Liteapp 滚动结束事件
      isLiteapp,            // 是否为 liteapp 环境
      scrollLeft,           // scroll-view 滚动位置
      scrollWithAnimation,  // scroll-view 动画开关
    };
  },
});
</script>

